import { DeleteOutlined } from '@ant-design/icons';
import { Table, Popconfirm, Checkbox, Icon } from 'antd';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { communicationStatus } from '~constants';
import * as actions from '~actions/special-offer/offer-map';
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import update from 'immutability-helper';
import { connect } from 'react-redux';
import { uniq, uniqBy, isEqual } from 'lodash';
import { ScrollLoadCheckBoxGroup } from '../property-list-editor/components/select-property-by-landlord/landlord-selector';
import { QUERY_LANDLORD } from './queries';

const mapStateToProps = state => ({
  communication: state.dashboard.specialOfferMap.get('communication')?.toJS(),
  listCommunication: state.dashboard.specialOfferList.get('communication')?.toJS(),
});

const mapDispatchToProps = dispatch => ({
  initialize: () => {
    dispatch(actions.initialize());
  },
});

@connect(mapStateToProps, mapDispatchToProps)
@withRouter
@withTranslation()
export default class PropertyList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedRowKeys: [],
      checkedAll: false,
      showLandlordSelector: false,
      tableSources: [],
      landlordSelectorParams: {
        pageNumber: 1,
        selectedLandlordList: [],
      },
    };
  }

  componentDidMount() {
    this.updateTableData();
  }

  componentDidUpdate() {
    this.updateTableData();
    this.updateSelectAll();
  }

  unlinkOffer = record => {
    let formedParams = {
      id: this.props.offerInfo.id,
      propertyIds: null,
    };
    formedParams = update(formedParams, { propertyIds: { $set: record.key } });
    this.props.unLinkSpecialOffer(formedParams);
  };

  tableColumns = () => [
    {
      title: this.props.t('cms.special_offer.column.property'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: this.props.t('cms.special_offer.column.landlord'),
      dataIndex: 'landlord',
      key: 'landlord',
      className: 'landlord-name',
      render: (_text, record) => {
        return record?.property?.landlord?.name || '';
      },
    },
    {
      title: this.props.t('cms.special_offer.column.actions'),
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right',
      width: 118,
      render: (_text, record) => (
        <div>
          <Popconfirm
            overlayClassName="special-offer__delete-btn"
            title={this.props.t('cms.special_offer.map.property.delete_remind')}
            onConfirm={() => {
              this.unlinkOffer(record);
            }}
            placement="topRight"
            okText={this.props.t('cms.special_offer.map.property.delete_confirm')}
            cancelText={this.props.t('cms.special_offer.map.property.delete_cancel')}
            arrowPointAtCenter
          >
            <a
              href="javascirpt:;"
              style={{ fontSize: 16 }}
              className="special-offer__children-delete-btn"
            >
              <DeleteOutlined />
            </a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  buildTableLandlordFilterData = () => {
    const tableSources = formatRowData(this.props.properties);
    return uniqBy(
      tableSources.map(item => ({
        label: item.property?.landlord?.name,
        value: item.property?.landlord?.id,
      })),
      item => {
        return item.value;
      },
    );
  };

  handleTableChange = pagination => {
    if (pagination.current !== this.props.pagination.pageNumber) {
      this.setState({ currentPage: pagination.current });
      this.props.setPagination(p => ({
        ...p,
        pageNumber: pagination.current,
      }));
    }
    if (pagination.pageSize !== this.props.pagination.pageSize) {
      this.props.setPagination(p => ({
        ...p,
        pageSize: pagination.pageSize,
      }));
    }
  };

  updateSelectAll = () => {
    const currentPagePropertyKeys = formatRowData(this.props.properties).map(pp => pp.key);

    let calcCheckedValue = null;
    if (
      currentPagePropertyKeys.length &&
      currentPagePropertyKeys.every(key => this.state.selectedRowKeys.includes(key))
    ) {
      calcCheckedValue = true;
    } else {
      calcCheckedValue = false;
    }

    if (this.state.checkedAll !== calcCheckedValue) {
      this.setState({ checkedAll: calcCheckedValue });
    }
  };

  handleSelectAll = e => {
    const checked = e.target.checked;
    const allPropertyKeys = this.state.tableSources.map(pp => pp.key);
    if (!allPropertyKeys.length) return;

    function* chunks(arr, n) {
      for (let i = 0; i < arr.length; i += n) {
        yield arr.slice(i, i + n);
      }
    }

    const allRowKeysGroupByPagesize = [...chunks(allPropertyKeys, this.props.pagination.pageSize)];
    if (checked) {
      this.setState({
        selectedRowKeys: uniq(
          this.state.selectedRowKeys.concat(
            allRowKeysGroupByPagesize[this.props.pagination.pageNumber - 1],
          ),
        ),
      });
    } else {
      this.setState({
        selectedRowKeys: uniq(
          this.state.selectedRowKeys.filter(
            key => !allRowKeysGroupByPagesize[this.props.pagination.pageNumber - 1]?.includes(key),
          ),
        ),
      });
    }

    this.setState({ checkedAll: checked });
  };

  handleBulkDelete = () => {
    const formedParams = {
      id: this.props.offerInfo.id,
      propertyIds: this.state.selectedRowKeys,
    };
    this.props.unLinkSpecialOffer(formedParams);
    this.props.setPagination(p => ({
      ...p,
      landlordIds: null,
      pageNumber: 1,
    }));
    this.setState({
      selectedRowKeys: [],
      checkedAll: false,
    });
  };

  updateTableData = () => {
    const tableSources = formatRowData(this.props.properties);

    if (!isEqual(this.state.tableSources, tableSources)) {
      this.setState({
        tableSources: tableSources,
      });
    }
  };

  render() {
    const { tableSources } = this.state;
    const { t } = this.props;
    const rowSelection = {
      selectedRowKeys: this.state.selectedRowKeys,
      onChange: selectedRowKeys => {
        this.setState({ selectedRowKeys: selectedRowKeys });
      },
    };

    return (
      <div className="special-offer-property-list">
        <div className="table-list__header">
          <div className="table-list__header__left">
            <Checkbox
              checked={this.state.checkedAll}
              className="table-list__header__select-all"
              onChange={this.handleSelectAll}
            />
            <span className="table-list__header__select-all__label">
              {t('cms.special_offer.table.filter.select_all')}
            </span>
          </div>

          <div className="table-list__header__right">
            <Popconfirm
              title={this.props.t('cms.special_offer.map.property.delete_remind')}
              onConfirm={this.handleBulkDelete}
              placement="topRight"
              okText={this.props.t('cms.special_offer.map.property.delete_confirm')}
              cancelText={this.props.t('cms.special_offer.map.property.delete_cancel')}
              arrowPointAtCenter
              disabled={this.state.selectedRowKeys.length === 0}
            >
              <span
                className={classNames('table-list__header__bulk-delete', {
                  'table-list__header__bulk-delete--disabled':
                    this.state.selectedRowKeys.length === 0,
                })}
              >
                {t('cms.special_offer.table.btn.bulk_delete')}
              </span>
            </Popconfirm>

            <span className="table-list__header__split" />
            <div>
              <span
                className="table-list__header__filter"
                onClick={() => {
                  this.setState({ showLandlordSelector: !this.state.showLandlordSelector });
                }}
              >
                {t('cms.special_offer.table.filter.filter_by_landlord')}
                <Icon
                  type="down"
                  style={{
                    fontSize: '12px',
                    marginLeft: '8px',
                    transform: this.state.showLandlordSelector ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </span>

              <div className="table-list__header__filter-container">
                <div>
                  <LandlordSearch
                    isShowFilterTable={this.state.showLandlordSelector}
                    setShowFilterTable={bool => {
                      this.setState({ showLandlordSelector: bool });
                    }}
                    value={this.state.selectedLandlord}
                    defaultQueryParams={this.state.landlordSelectorParams}
                    onConfirm={({ ids, selectedLandlordList, currentPageNumber }) => {
                      this.props.setPagination(p => ({
                        ...p,
                        pageNumber: 1,
                        landlordIds: ids,
                      }));
                      this.setState({
                        landlordSelectorParams: {
                          selectedLandlordList,
                          currentPageNumber,
                        },
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="table-list__wrapper">
          <Table
            rowSelection={rowSelection}
            showHeader={false}
            columns={this.tableColumns()}
            className="table-list"
            dataSource={tableSources}
            pagination={{
              total: this.props.pageInfo.totalCount,
              current: this.props.pagination.pageNumber,
              pageSize: this.props.pagination.pageSize,
              showSizeChanger: true,
            }}
            loading={
              this.props.communication.link.status === communicationStatus.FETCHING ||
              this.props.communication.unlink.status === communicationStatus.FETCHING ||
              this.props.loading
            }
            onChange={this.handleTableChange}
          />
        </div>
      </div>
    );
  }
}

function LandlordSearch(props) {
  const { onConfirm, value, isShowFilterTable, setShowFilterTable, defaultQueryParams } = props;
  const [selectedLandlord, setSelectedLandlord] = useState(value || []);
  const [currentPageNumber, setPageNumber] = useState(1);
  const [searchLandlord, setSearchLandlord] = useState('');
  const [landlordList, setLandlordList] = useState([]);
  const [selectedLandlordList, setSelectedLandlordList] = useState(
    defaultQueryParams.selectedLandlordList || [],
  );
  const lastTimeSearch = useRef('');

  const { data: landlordData, loading: getLandlordLoading } = useQuery(QUERY_LANDLORD, {
    variables: {
      nameLike: searchLandlord || null,
      pageNumber: currentPageNumber,
      pageSize: 20,
    },
    fetchPolicy: 'network-only',
  });

  const filterTableDataSource = useMemo(() => {
    const withSelectedLandlordNewList = uniqBy(
      selectedLandlordList.concat(landlordList),
      landlord => landlord.id,
    );
    return withSelectedLandlordNewList.map(landlord => ({
      label: landlord?.name,
      value: landlord?.id,
    }));
  }, [landlordList]);

  const handleFetchMore = useCallback(() => {
    if (landlordData?.landlords?.pageInfo?.hasNextPage && !getLandlordLoading) {
      setPageNumber(currentPageNumber => currentPageNumber + 1);
    }
  }, [landlordData, getLandlordLoading]);

  const handleOnSearch = v => {
    setPageNumber(1);
    setSearchLandlord(v);
  };

  const onChange = ids => {
    setSelectedLandlord(ids);
    onConfirm({
      ids,
      selectedLandlordList,
      currentPageNumber,
    });
  };

  const handleOnSelect = ids => {
    const landlordListWithSelectedLandlord = uniqBy(
      selectedLandlordList.concat(landlordList),
      landlord => landlord.id,
    );
    setSelectedLandlordList(
      landlordListWithSelectedLandlord.filter(landlord => ids.includes(landlord.id)),
    );
  };

  const handleReset = () => {
    setSelectedLandlordList([]);
    onConfirm({
      ids: [],
      selectedLandlordList: [],
      currentPageNumber: 1,
    });
  };

  useEffect(() => {
    if (landlordData && !getLandlordLoading) {
      const newestLandlordList = landlordData?.landlords?.edges?.map(item => item.node);
      if (landlordList.length && searchLandlord === lastTimeSearch.current) {
        setLandlordList(uniqBy(landlordList.concat(newestLandlordList), landlord => landlord.id));
      } else {
        setLandlordList(newestLandlordList);
      }

      lastTimeSearch.current = searchLandlord;
    }
  }, [!!landlordData]);

  return (
    <ScrollLoadCheckBoxGroup
      loading={getLandlordLoading}
      list={filterTableDataSource}
      value={selectedLandlord}
      visible={isShowFilterTable}
      onClose={setShowFilterTable}
      onChange={onChange}
      onReachEndList={handleFetchMore}
      onSearch={handleOnSearch}
      onSelect={handleOnSelect}
      onReset={handleReset}
      top={26}
    />
  );
}

export function formatRowData(properties = []) {
  let data = [];
  let allProperties = update([], { $set: properties });

  allProperties.forEach((property, index) => {
    data = update(data, {
      [index]: {
        $set: {
          name: property.name,
          key: property.id,
          property,
        },
      },
    });
  });

  return data;
}

PropertyList.propTypes = {
  t: PropTypes.func.isRequired,
  communication: PropTypes.shape({
    create: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
    update: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
    delete: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
    list: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
    link: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
    unLink: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
  }),
  offerInfo: PropTypes.object.isRequired,
  unLinkSpecialOffer: PropTypes.func.isRequired,
};

PropertyList.defaultProps = {
  t: () => {},
  initialize: () => {},
  offerInfo: {},
  communication: {
    create: {
      status: '',
    },
    update: {
      status: '',
    },
    delete: {
      status: '',
    },
    list: {
      status: '',
    },
    link: {
      status: '',
    },
    unLink: {
      status: '',
    },
  },
  history: {
    push: () => {},
  },
  unLinkSpecialOffer: () => {},
};
